import Close from '@ixigo/icons/Close';
import InfoCircle from '@ixigo/icons/InfoCircle';
import Button from '@ixigo/iui/Button';
import { InlineAlert } from '@ixigo/iui/InlineAlert';
import {
	DateTimePicker as DateTimePickerComponent,
	type DateTimePickerProps,
	ResponsiveModal,
} from '@repo/shared/components';
import { useMemo, useState } from 'react';
import { NavigationHeader } from '../NavigationHeader';

export interface DateTimePickerSheetProps {
	open: boolean;
	onClose: () => void;
	alertMsg?: string;
	minDurationInMinutes?: number;
	defaultValue?: DateTimePickerProps['defaultValue'];
	onConfirm: (value: Date) => void;
}

export function DateTimePickerSheet({
	alertMsg,
	minDurationInMinutes,
	defaultValue,
	onConfirm,
	...bottomsheetProps
}: DateTimePickerSheetProps) {
	// cab selection should be allowed only if the min date is minDurationInMinutes or 30 minutes from now & max date is 3 months from min date
	const range = useMemo(() => {
		const minDate = new Date(
			Date.now() + (minDurationInMinutes || 30) * 60 * 1000,
		);
		const maxDate = new Date(minDate.getTime() + 3 * 30 * 24 * 60 * 60 * 1000);
		return { min: minDate, max: maxDate };
	}, [minDurationInMinutes]);
	const [value, setValue] = useState<Date>(
		defaultValue
			? defaultValue instanceof Date
				? defaultValue
				: new Date(defaultValue)
			: range.min,
	);

	return (
		<ResponsiveModal {...bottomsheetProps}>
			<NavigationHeader
				title="Pickup Time"
				leftIcon={<Close />}
				leftAction={bottomsheetProps.onClose}
				className="border-b border-tertiary"
			/>
			<div className="flex flex-col p-20 gap-y-20">
				{alertMsg && (
					<InlineAlert
						title={alertMsg}
						severity="warning"
						textColor="neutral"
						variant="moderate"
						icon={<InfoCircle className="size-20 text-primary" />}
					/>
				)}
				<DateTimePickerComponent
					defaultValue={defaultValue}
					onChange={setValue}
					step={5}
					selectionMode="composite"
					{...range}
				/>
			</div>
			<div className="px-20 py-2.5 border-t border-tertiary">
				<Button onClick={() => onConfirm(value)} size="lg" className="w-full">
					Confirm
				</Button>
			</div>
		</ResponsiveModal>
	);
}
